/**
 * BatchPaymentDialog — one wire, several invoices. Each line settles its own
 * full balance unless you type a partial amount. One payment, one remittance.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Banknote, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/App";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BatchPaymentDialog({ invoices, token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [methods, setMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({
    method: "wire", payment_date: new Date().toISOString().slice(0, 10),
    reference: "", bank_account_id: "", notes: "",
  });

  useEffect(() => {
    setLines((invoices || []).map((i) => ({
      invoice_id: i.id, invoice_number: i.invoice_number, supplier_name: i.supplier_name,
      balance: Number(i.balance) || 0, full: true, amount: "",
    })));
  }, [invoices]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/payment-runs/settings`, { headers });
        setMethods(r.data.methods || []);
        const accounts = r.data.settings?.bank_accounts || [];
        setBanks(accounts);
        const def = accounts.find((a) => a.default) || accounts[0];
        if (def) setForm((f) => ({ ...f, bank_account_id: def.id }));
      } catch { /* quiet */ }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!invoices?.length) return null;
  const suppliers = [...new Set(invoices.map((i) => i.supplier_name))];
  const total = lines.reduce((s, l) => s + (l.full ? l.balance : Number(l.amount) || 0), 0);

  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (suppliers.length > 1) {
      toast.error("One payment can only cover one supplier — pay each separately");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payment-runs/record-batch-payment`, {
        lines: lines.map((l) => ({
          invoice_id: l.invoice_id,
          amount: l.full ? null : Number(l.amount) || 0,
        })),
        method: form.method, payment_date: form.payment_date, reference: form.reference,
        bank_account_id: form.bank_account_id || null, notes: form.notes,
      }, { headers });
      toast.success(`${money(r.data.payment.amount)} recorded across ${r.data.lines.length} invoice(s) — ${r.data.settled_in_full} closed in full`);
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record that payment");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="batch-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record one payment across {invoices.length} invoices</DialogTitle>
          <DialogDescription>
            Confirm full or partial against each invoice. This saves as a single remittance.
          </DialogDescription>
        </DialogHeader>

        {suppliers.length > 1 && (
          <p className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded p-2"
            data-testid="batch-mixed-supplier">
            You have picked invoices from {suppliers.length} suppliers ({suppliers.join(", ")}).
            A single wire is a single remittance — pay each supplier separately.
          </p>
        )}

        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={l.invoice_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2"
              data-testid={`batch-line-${l.invoice_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-bold text-slate-900">{l.supplier_name}</p>
                <p className="text-[11px] text-slate-500 font-mono">
                  {l.invoice_number} · {money(l.balance)} owed
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" data-testid={`batch-full-${l.invoice_id}`}
                  className={`text-[10px] font-black uppercase px-2 py-1 rounded border ${l.full
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-300"}`}
                  onClick={() => set(i, { full: true, amount: "" })}>
                  Full
                </button>
                <button type="button" data-testid={`batch-partial-${l.invoice_id}`}
                  className={`text-[10px] font-black uppercase px-2 py-1 rounded border ${!l.full
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-slate-600 border-slate-300"}`}
                  onClick={() => set(i, { full: false, amount: l.amount || "" })}>
                  Partial
                </button>
              </div>
              <Input className="h-8 w-28 text-xs" type="number" step="0.01" disabled={l.full}
                value={l.full ? l.balance.toFixed(2) : l.amount} data-testid={`batch-amount-${l.invoice_id}`}
                onChange={(e) => set(i, { amount: e.target.value })} />
            </div>
          ))}
        </div>

        <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-2.5">
          <p className="text-sm font-black text-emerald-900" data-testid="batch-total">
            Payment total {money(total)}
          </p>
          <p className="text-[11px] text-emerald-800">
            {lines.filter((l) => l.full).length} invoice(s) closing in full ·{" "}
            {lines.filter((l) => !l.full).length} partial
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Method</label>
            <select value={form.method} data-testid="batch-method"
              className="h-9 w-full text-sm border border-slate-300 rounded-md px-2"
              onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
              {methods.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Payment date</label>
            <Input type="date" value={form.payment_date} data-testid="batch-date"
              onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Confirmation #</label>
            <Input value={form.reference} placeholder="20261007-00005015" data-testid="batch-reference"
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Paid from</label>
            <select value={form.bank_account_id} data-testid="batch-bank"
              className="h-9 w-full text-sm border border-slate-300 rounded-md px-2"
              onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}>
              <option value="">— not recorded —</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.institution}{b.nickname ? ` · ${b.nickname}` : ""}{b.last4 ? ` ••••${b.last4}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Textarea rows={2} value={form.notes} placeholder="Notes (optional)" data-testid="batch-notes"
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save}
            disabled={busy || suppliers.length > 1} data-testid="batch-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1" />}
            Record {money(total)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
